import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const res = await api.get("/auth/me");
            setUser(res.data);
        } catch (e) {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const res = await api.post("/auth/login", { email, password });
        // Auth is carried by the httpOnly session_token cookie set by the backend.
        setUser(res.data.user);
        return res.data.user;
    };

    const register = async (name, email, password) => {
        const res = await api.post("/auth/register", { name, email, password });
        setUser(res.data.user);
        return res.data.user;
    };

    const finalizeGoogleSession = async (session_id) => {
        const res = await api.post("/auth/google-session", { session_id });
        setUser(res.data.user);
        return res.data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch (e) { console.debug("Logout request failed; clearing local session anyway", e); }
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, setUser, loading, login, register, logout, finalizeGoogleSession, refresh: checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
}
